import { ReactNode, useState } from "react";
import { Typography } from "@mui/material";
import { ThemeWrapper } from "@verity/components";
import { CoreButton, CoreModal, CoreSwitch } from "@verity/components/core";
import apiClient from "../../../../helpers/api-client";
import i18n from "../../../../i18n";
import { CustomSettingData } from "../../settings-page";
import styles from "./index.module.css";

type ConfirmationModalProps = {
	onClose: () => void;
	onConfirm: () => void;
};

const ConfirmationModal = ({
	onClose,
	onConfirm,
	title,
	children,
}: ConfirmationModalProps & {
	title: ReactNode;
	children: ReactNode;
}) => {
	return (
		<CoreModal
			open
			onClose={onClose}
			title={title}
			buttons={
				<>
					<CoreButton color="secondary" onClick={onClose}>
						{i18n.t("portal.components.settings.chromeOsRequireManagedGuestSession.confirmationModal.cancel")}
					</CoreButton>
					<CoreButton
						onClick={() => {
							onConfirm();
							onClose();
						}}
					>
						{i18n.t("portal.components.settings.chromeOsRequireManagedGuestSession.confirmationModal.confirm")}
					</CoreButton>
				</>
			}
		>
			<div className={styles.confirmationModalBody}>
				{children}
				<Typography>
					{i18n.t("portal.components.settings.chromeOsRequireManagedGuestSession.confirmationModal.continue")}
				</Typography>
			</div>
		</CoreModal>
	);
};

const EnableConfirmationModal = (confirmationModalProps: ConfirmationModalProps) => {
	return (
		<ConfirmationModal
			{...confirmationModalProps}
			title={i18n.t("portal.components.settings.chromeOsRequireManagedGuestSession.confirmationModal.enable.title")}
		>
			<Typography>
				{i18n.t("portal.components.settings.chromeOsRequireManagedGuestSession.confirmationModal.enable.description1")}
			</Typography>
			<Typography>
				<strong>
					{i18n.t("portal.components.settings.chromeOsRequireManagedGuestSession.confirmationModal.enable.note")}{" "}
				</strong>
				{i18n.t("portal.components.settings.chromeOsRequireManagedGuestSession.confirmationModal.enable.description2")}
			</Typography>
		</ConfirmationModal>
	);
};

const DisableConfirmationModal = (confirmationModalProps: ConfirmationModalProps) => {
	return (
		<ConfirmationModal
			{...confirmationModalProps}
			title={i18n.t("portal.components.settings.chromeOsRequireManagedGuestSession.confirmationModal.disable.title")}
		>
			<Typography>
				{i18n.t("portal.components.settings.chromeOsRequireManagedGuestSession.confirmationModal.disable.description")}
			</Typography>
		</ConfirmationModal>
	);
};

export const ChromeOsRequireManagedGuestSessionInput = ({
	loadData,
	setting,
	onSettingChange,
	disabled,
}: {
	loadData: { lockdownRequireManagedGuestSession: boolean };
	setting: CustomSettingData<boolean>;
	onSettingChange: (value?: boolean) => void;
	disabled?: boolean;
}) => {
	const [requireManagedGuestSession, setRequireManagedGuestSession] = useState(
		setting.value ?? loadData.lockdownRequireManagedGuestSession,
	);

	const [confirmationModal, setConfirmationModal] = useState<"enable" | "disable" | undefined>(undefined);

	const closeConfirmationModal = () => setConfirmationModal(undefined);
	const updateSettings = (enforce: boolean) => {
		setRequireManagedGuestSession(enforce);
		onSettingChange(enforce);
	};

	return (
		<ThemeWrapper>
			<div style={{ maxWidth: "100%", overflowX: "auto" }}>
				<div style={{ minWidth: "420px" }}>
					<div style={{ display: "flex", paddingRight: "5px" }}>
						<div style={{ marginRight: "15px" }}>
							<Typography fontSize={14} lineHeight={1.6}>
								{i18n.t("portal.components.settings.chromeOsRequireManagedGuestSession.description")}
							</Typography>
						</div>
						<CoreSwitch
							checked={requireManagedGuestSession}
							onChange={(e) => {
								setConfirmationModal(e.target.checked ? "enable" : "disable");
							}}
							disabled={disabled}
							labelTextType="left"
						/>
					</div>
				</div>
			</div>
			{confirmationModal === "enable" && (
				<EnableConfirmationModal onClose={closeConfirmationModal} onConfirm={() => updateSettings(true)} />
			)}
			{confirmationModal === "disable" && (
				<DisableConfirmationModal onClose={closeConfirmationModal} onConfirm={() => updateSettings(false)} />
			)}
		</ThemeWrapper>
	);
};

export async function saveChromeOsRequireManagedGuestSession(
	instituteUuid?: string,
	requireManagedGuestSession?: boolean,
) {
	try {
		if (instituteUuid) {
			if (typeof requireManagedGuestSession === "boolean") {
				await apiClient.institute.updateSettings(instituteUuid, {
					lockdownRequireManagedGuestSession: requireManagedGuestSession,
				});
			}
		} else {
			throw new Error("no instituteId");
		}
		return { ok: true };
	} catch (error) {
		console.error(error);
		return { ok: false, reason: "Failed to save enforce managed guest session setting." };
	}
}
